// src/modules/communications/components/CallModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaPhone, 
  FaPhoneSlash, 
  FaSpinner, 
  FaUser 
} from 'react-icons/fa';
import Modal from '../../../components/shared/Modal';
import { useCommunication } from '../context/CommunicationContext';
import { formatPhoneNumber } from '../utils/formatUtils';
import Input from '../../../components/shared/Input';

const CallDetails = styled.div`
  margin-bottom: ${props => props.theme.spacing(3)};
  padding: ${props => props.theme.spacing(2)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
`;

const CallInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.5rem;
  }
`;

const PhoneNumber = styled.span`
  font-size: ${props => props.theme.typography.fontSize.large};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const CustomerName = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const CallFlow = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing(3)};
  border-radius: ${props => props.theme.borderRadius};
  margin: ${props => props.theme.spacing(3)} 0;
`;

const FlowTitle = styled.h4`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 ${props => props.theme.spacing(2)} 0;
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const FlowStep = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:before {
    content: '${props => props.step}';
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.black};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(3)};
`;

const CallButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.black};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  cursor: pointer;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: all 0.2s ease;
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.inputGray};
  color: ${props => props.theme.colors.textPrimary};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.surface};
  }
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.success};
  background: ${props => props.theme.colors.success}22;
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  margin-top: ${props => props.theme.spacing(2)};
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const RememberChoice = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-top: ${props => props.theme.spacing(2)};
  cursor: pointer;

  input[type="checkbox"] {
    margin: 0;
  }
`;

/**
 * Call Modal component for click-to-call functionality
 * @returns {React.ReactElement} Call Modal component
 */
const CallModal = () => {
  const { 
    callModalOpen, 
    closeCallModal, 
    callTarget,
    initiateCall
  } = useCommunication();
  
  const [userPhone, setUserPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [rememberPhone, setRememberPhone] = useState(true);

  // Load saved phone number
  useEffect(() => {
    const savedPhone = localStorage.getItem('userPhoneNumber');
    if (savedPhone) {
      setUserPhone(savedPhone);
    }
  }, []);

  const handleCall = async () => {
    if (!userPhone.trim()) return;

    setIsLoading(true);
    setSuccess(false);

    try {
      // Save phone number for future use if checkbox is checked
      if (rememberPhone) {
        localStorage.setItem('userPhoneNumber', userPhone);
      }

      await initiateCall(userPhone, notes);
      setSuccess(true);
      
      // Close modal after a short delay
      setTimeout(() => {
        closeCallModal();
        setSuccess(false);
      }, 3000);
    } catch (error) {
      // Error is handled in the context
      console.error('Error in CallModal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!callModalOpen) return null;

  return (
    <Modal
      title="Place Call"
      isOpen={callModalOpen}
      onCancel={closeCallModal}
      onConfirm={null}
      hideFooter
    >
      <CallDetails>
        <CallInfo>
          <FaPhone />
          <div>
            <div>Calling:</div>
            <PhoneNumber>{formatPhoneNumber(callTarget.phone)}</PhoneNumber>
          </div>
        </CallInfo>

        {callTarget.name && (
          <CustomerName>
            <FaUser />
            {callTarget.name}
          </CustomerName>
        )}
      </CallDetails>

      <Input
        label="Your Phone Number"
        icon={<FaPhone />}
        value={userPhone}
        onChange={(e) => setUserPhone(e.target.value)}
        placeholder="Enter your phone number"
        disabled={isLoading || success}
      />

      <RememberChoice>
        <input
          type="checkbox"
          checked={rememberPhone}
          onChange={(e) => setRememberPhone(e.target.checked)}
          disabled={isLoading || success}
        />
        Remember my phone number
      </RememberChoice>

      <Input
        label="Call Notes (Optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add any notes about this call"
        disabled={isLoading || success}
      />

      {success && (
        <SuccessMessage>
          Your phone will ring momentarily to connect the call.
        </SuccessMessage>
      )}

      <ButtonContainer>
        <CallButton
          onClick={handleCall}
          disabled={!userPhone.trim() || isLoading || success}
        >
          {isLoading ? (
            <>
              <FaSpinner className="icon-spin" />
              Initiating...
            </>
          ) : success ? (
            'Call Initiated'
          ) : (
            <>
              <FaPhone />
              Start Call
            </>
          )}
        </CallButton>

        <CancelButton
          onClick={closeCallModal}
          disabled={isLoading}
        >
          {success ? 'Close' : 'Cancel'}
        </CancelButton>
      </ButtonContainer>

      <CallFlow>
        <FlowTitle>How it works:</FlowTitle>
        <FlowStep step="1">Your phone will ring first</FlowStep>
        <FlowStep step="2">When you answer, you'll hear "Connecting your call"</FlowStep>
        <FlowStep step="3">You'll be connected to the customer</FlowStep>
        <FlowStep step="4">Call will be automatically recorded and logged</FlowStep>
      </CallFlow>
    </Modal>
  );
};

export default CallModal;